import React, { useCallback, useEffect, useRef, useState } from 'react';
import { type CommunicationRecord } from '@entities';
import { communicationRecordRepo } from '@repositories';
import {
    Box,
    Button,
    List,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import { useUser } from '../../contexts/UserProvider';
import { CommunicationSkeleton } from './CommunicationSkeleton';
import { CommunicationCard } from './CommunicationCard';
import { CreateRecordDialog } from './CreateRecordDialog';
import { ViewRecordDialog } from './ViewRecordDialog';
import { AddComment } from '@mui/icons-material';
import { COMMUNICATION_RECORDS_PREVIEW_LIMIT } from '@shared/helpers/communication-record';
import { partnerOrgCommunicationRecordsWhere } from '@shared/helpers/partner-organization-communication';
import { volunteerOrgCommunicationRecordsWhere } from '@shared/helpers/volunteer-organization';
import {
    isCommunicationChannel,
} from '@shared/helpers/communication-channel';

export const CommunicationRecordManager: React.FC<{
    associationId?: string;
    groupId?: string;
    opportunityId?: string;
    summerCampaignLeadId?: string;
    partnerOrganizationId?: string;
    volunteerOrganizationId?: string;
    type?: 'volunteer' | 'aid-related' | 'opportunity' | 'lead' | 'partner-organization' | 'volunteer-organization';
    showAllRecords?: boolean;
    embedded?: boolean;
    onSave?: (records?: CommunicationRecord[]) => void;
    previewRecords?: CommunicationRecord[];
    tourAnchors?: boolean;
}> = ({
    associationId,
    groupId,
    opportunityId,
    summerCampaignLeadId,
    partnerOrganizationId,
    volunteerOrganizationId,
    type,
    showAllRecords = false,
    embedded = false,
    onSave,
}) => {
    const [records, setRecords] = useState<CommunicationRecord[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [description, setDescription] = useState('');
    const [title, setTitle] = useState('');
    const [channel, setChannel] = useState('');
    const [selectedRecord, setSelectedRecord] = useState<CommunicationRecord | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showSkeleton, setShowSkeleton] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const { currentUser } = useUser();
    const loadRequestIdRef = useRef(0);

    const hasTarget = Boolean(
        associationId || groupId || opportunityId || summerCampaignLeadId || partnerOrganizationId || volunteerOrganizationId,
    );

    const loadRecords = useCallback(async () => {
        const requestId = ++loadRequestIdRef.current;

        if (!hasTarget) {
            setRecords([]);
            setIsLoading(false);
            return [];
        }

        setIsLoading(true);
        setShowSkeleton(false);
        try {
            const where =
                summerCampaignLeadId && associationId
                    ? {
                          $or: [
                              { summerCampaignLeadId },
                              { associationId },
                          ],
                      }
                      : partnerOrganizationId
                        ? partnerOrgCommunicationRecordsWhere(partnerOrganizationId)
                      : volunteerOrganizationId
                        ? volunteerOrgCommunicationRecordsWhere(volunteerOrganizationId)
                      : {
                          ...(associationId ? { associationId } : {}),
                          ...(groupId ? { groupId } : {}),
                          ...(opportunityId ? { opportunityId } : {}),
                          ...(summerCampaignLeadId ? { summerCampaignLeadId } : {}),
                          ...(partnerOrganizationId ? { partnerOrganizationId } : {}),
                          ...(volunteerOrganizationId ? { volunteerOrganizationId } : {}),
                      };

            const loadedRecords = await communicationRecordRepo.find({
                where,
                orderBy: { createdAt: 'desc' },
                include: { manager: true, opportunity: true },
                ...(showAllRecords ? {} : { limit: COMMUNICATION_RECORDS_PREVIEW_LIMIT }),
            });
            if (requestId !== loadRequestIdRef.current) return [];
            setRecords(loadedRecords);
            return loadedRecords;
        } catch (error) {
            if (requestId !== loadRequestIdRef.current) return [];
            console.error('Error loading records:', error);
            setRecords([]);
            return [];
        } finally {
            if (requestId === loadRequestIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [associationId, groupId, opportunityId, summerCampaignLeadId, partnerOrganizationId, volunteerOrganizationId, hasTarget, showAllRecords]);

    const handleRecordsChanged = useCallback(() => {
        void (async () => {
            const loaded = await loadRecords();
            onSave?.(loaded);
        })();
    }, [loadRecords, onSave]);

    useEffect(() => {
        void loadRecords();
    }, [loadRecords]);

    useEffect(() => {
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        if (isLoading) {
            timeoutId = setTimeout(() => {
                setShowSkeleton(true);
            }, 300);
        } else {
            setShowSkeleton(false);
        }
        return () => {
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [isLoading]);

    const handleCreateRecord = async () => {
        setSaveError(null);
        try {
            if (!hasTarget) {
                setSaveError('לא ניתן לשמור תיעוד ללא שיוך לליד, מתנדב, קבוצה, משימה, ארגון שותף או ארגון מתנדבים.');
                return;
            }

            if (!title.trim()) {
                setSaveError('כותרת היא שדה חובה.');
                return;
            }
            if (!currentUser?.id) {
                setSaveError('לא נמצא מנהל מחובר.');
                return;
            }

            await communicationRecordRepo.insert({
                title: title.trim(),
                description,
                ...(associationId ? { associationId } : {}),
                ...(groupId ? { groupId } : {}),
                ...(opportunityId ? { opportunityId } : {}),
                ...(summerCampaignLeadId ? { summerCampaignLeadId } : {}),
                ...(partnerOrganizationId ? { partnerOrganizationId } : {}),
                ...(volunteerOrganizationId ? { volunteerOrganizationId } : {}),
                type: type || "",
                ...((type === "partner-organization" || type === "volunteer-organization") && isCommunicationChannel(channel)
                    ? { channel }
                    : {}),
                managerId: currentUser.id,
            });

            setTitle('');
            setDescription('');
            setChannel('');
            setIsDialogOpen(false);
            handleRecordsChanged();
        } catch (error) {
            console.error('Error creating record:', error);
            setSaveError('שגיאה בשמירת התיעוד. נסו שוב.');
        }
    };

    const handleRecordClick = (record: CommunicationRecord) => {
        setSelectedRecord(record);
    };

    return (
        <Stack className="communication-record-manager" sx={{ width: '98%', margin: '0 auto' }}>
            <Box>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: embedded ? 'flex-end' : 'space-between',
                        alignItems: 'center',
                        mb: embedded ? 2 : 4,
                        px: embedded ? 2 : 0,
                        pt: embedded ? 2 : 0,
                        borderRadius: 2,
                    }}
                >
                    {!embedded && (
                        <Typography variant="h5" fontWeight="bold" color="primary">
                            תיעוד תקשורת
                        </Typography>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<AddComment />}
                        onClick={() => setIsDialogOpen(true)}
                        sx={{
                            borderRadius: 3,
                            px: 3,
                            py: 1,
                            boxShadow: embedded ? 0 : 2,
                            '&:hover': {
                                boxShadow: embedded ? 0 : 4,
                            },
                        }}
                    >
                        הוספת תיעוד
                    </Button>
                </Box>

                {showSkeleton ? (
                    <List sx={{ p: 0 }}>
                        {[1, 2, 3].map((index) => (
                            <CommunicationSkeleton key={index} />
                        ))}
                    </List>
                ) : !isLoading && records.length === 0 ? (
                    <Paper
                        className="no-communication-container"
                        sx={{
                            p: 4,
                            textAlign: 'center',
                            borderRadius: 2,
                        }}
                    >
                        <Typography variant="h6" color="text.secondary">
                            {type === "partner-organization" || type === "volunteer-organization"
                              ? "אין תיעוד תקשורת מול הארגון"
                              : "לא קיימת תקשורת מתועדת מול אובייקט זה"}
                        </Typography>
                    </Paper>
                ) : (
                    <List sx={{ p: 0 }}>
                        {records.map((record) => (
                            <CommunicationCard
                                key={record.id}
                                record={record}
                                onClick={() => handleRecordClick(record)}
                            />
                        ))}
                    </List>
                )}

                {saveError && (
                    <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                        {saveError}
                    </Typography>
                )}

                <CreateRecordDialog
                    isOpen={isDialogOpen}
                    onClose={() => {
                        setSaveError(null);
                        setIsDialogOpen(false);
                    }}
                    title={title}
                    description={description}
                    setTitle={setTitle}
                    setDescription={setDescription}
                    channel={channel}
                    setChannel={setChannel}
                    showChannelPicker={type === "partner-organization" || type === "volunteer-organization"}
                    onSave={handleCreateRecord}
                />

                <ViewRecordDialog
                    record={selectedRecord}
                    setSelectedRecord={setSelectedRecord}
                    onClose={() => setSelectedRecord(null)}
                    onSave={handleRecordsChanged}
                />
            </Box>
        </Stack>
    );
};
